import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";

import { Dialog } from "primereact/dialog";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import ProgressiveControl from "../common/ProgressiveControl";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL } from "../../style";
import FieldNote from "./common/FieldNote";
import ManagedListFrame from "./common/ManagedListFrame";

export default function WebsiteHomeHighlights({ bare = false, title = "Highlight rows" } = {}) {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/home-highlights",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Home Highlights");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <ManagedListFrame bare={bare} title={title} highlights={["Each row is one highlight under the about story"]} onAdd={() => setDialog({ visible: true, record: {} })}>
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className={bare ? "flex flex-column gap-2" : "flex-1 overflow-y-scroll flex flex-column gap-2 p-2"}>
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </ManagedListFrame>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/home-highlights/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Home Highlights Deleted", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    const active = true;
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.id}. {row.title || "Untitled"}</span>
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_active: true, is_published: true, ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const save = () => {
        requestAPI({
            requestPath: "website/home-highlights",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Home Highlights Saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add" : "Edit"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="icon" className="w-full" value={record?.icon ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, icon: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="icon">Icon name</label>
                        </FloatLabel>
                        <FieldNote text="Material icon name, such as school. Leave blank to use a tick." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="title">Highlight title</label>
                        </FloatLabel>
                        <FieldNote text="Bold line in the row under the story." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                            <label htmlFor="description">Line under the title</label>
                        </FloatLabel>
                    </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
